import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { useApiClient } from '../hooks';
import apiClient from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import type { SendOTPRequest, SendOTPResponse } from '../types/api.types';

/**
 * Login page with phone number authentication
 */
export default function Login() {
    const navigate = useNavigate();
    const { loading: isLoading, error, callApi, setError } = useApiClient();
    const [phoneNumber, setPhoneNumber] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validate phone number (basic validation)
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(phoneNumber)) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }

        // Call send OTP API
        const requestData: SendOTPRequest = {
            phone: phoneNumber,
        };

        await callApi<SendOTPResponse>(
            () => apiClient.post(API_ENDPOINTS.AUTH.SEND_OTP, requestData),
            () => {
                // Success - navigate to OTP verification
                navigate('/verify-otp', { state: { phoneNumber } });
            },
            (errorMessage) => {
                // Error is already set by useApiClient
                console.error('Send OTP error:', errorMessage);
            }
        );
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#f1f5f9] to-[#e2e8f0] relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-[#6e64f0] opacity-10 rounded-full blur-3xl animate-pulse"></div>
                <div
                    className="absolute bottom-20 right-10 w-96 h-96 bg-[#10b981] opacity-10 rounded-full blur-3xl animate-pulse"
                    style={{ animationDelay: '1s' }}
                ></div>
                <div
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0e0858] opacity-5 rounded-full blur-3xl animate-pulse"
                    style={{ animationDelay: '2s' }}
                ></div>
            </div>

            {/* Login Card */}
            <div className="relative z-10 w-full max-w-md px-6">
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-[#e2e8f0] backdrop-blur-sm">
                    {/* Logo/Brand */}
                    <div className="flex justify-center mb-8">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6e64f0] to-[#8b7cf6] flex items-center justify-center shadow-lg">
                            <span className="text-white font-bold text-2xl">B</span>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-[#1e293b] mb-2">Welcome Back</h1>
                        <p className="text-[#64748b]">Enter your phone number to continue</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label
                                htmlFor="phone"
                                className="block text-sm font-medium text-[#1e293b] mb-2"
                            >
                                Phone Number
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                                    <span className="text-[#64748b] font-medium">+91</span>
                                </div>
                                <input
                                    type="tel"
                                    id="phone"
                                    value={phoneNumber}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        if (value.length <= 10) {
                                            setPhoneNumber(value);
                                            setError('');
                                        }
                                    }}
                                    placeholder="Enter 10-digit mobile number"
                                    className="w-full pl-16 pr-4 py-3 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent transition-all duration-200 text-[#1e293b] placeholder:text-[#94a3b8]"
                                    maxLength={10}
                                    autoFocus
                                />
                            </div>
                            {error && (
                                <p className="mt-2 text-sm text-[#ef4444] flex items-center gap-1">
                                    <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    {error}
                                </p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            size="large"
                            isLoading={isLoading}
                            disabled={phoneNumber.length !== 10}
                            className="w-full"
                        >
                            {isLoading ? 'Sending OTP...' : 'Continue'}
                        </Button>
                    </form>

                    {/* Footer */}
                    <div className="mt-8 text-center">
                        <p className="text-sm text-[#64748b]">
                            By continuing, you agree to our{' '}
                            <a href="#" className="text-[#6e64f0] hover:underline">
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a href="#" className="text-[#6e64f0] hover:underline">
                                Privacy Policy
                            </a>
                        </p>
                    </div>
                </div>

                {/* Additional Info */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-[#64748b]">
                        Need help?{' '}
                        <a href="#" className="text-[#6e64f0] hover:underline font-medium">
                            Contact Support
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}
